import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../utils/init-firebase';
import { getFeatureVersion } from '../constants/featureRegistry';

/**
 * Hook to manage feature highlight visibility
 * @param {string} featureId - The feature identifier from featureRegistry
 * @returns {Object} - { shouldShow: boolean, markAsSeen: () => void, loading: boolean }
 */
export const useFeatureHighlight = (featureId) => {
  const { currentUser, latestVersion } = useAuth();
  const [shouldShow, setShouldShow] = useState(false);
  const [loading, setLoading] = useState(true);

  // Get the version this feature was introduced in
  const introducedInVersion = getFeatureVersion(featureId);

  useEffect(() => {
    const checkFeatureVisibility = async () => {
      if (!currentUser || !latestVersion || !introducedInVersion) {
        setLoading(false);
        setShouldShow(false);
        return;
      }

      try {
        // Get user document to check seen features
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          const seenFeaturesByVersion = userData.seenFeaturesByVersion || {};

          // Only show feature highlight if current version exactly matches the version it was introduced in
          // This prevents new users from seeing old feature highlights
          const shouldShowFeature = latestVersion === introducedInVersion;
          
          if (shouldShowFeature) {
            // Check if user has already seen this feature for this version
            // Handle both correct structure (string key) and incorrect nested structure (backwards compatibility)
            let seenFeaturesForVersion = seenFeaturesByVersion[latestVersion];
            
            // If not found as string key, check for nested structure (e.g., 8.3.2 stored as 8 -> 3 -> 2)
            if (!seenFeaturesForVersion && latestVersion.includes('.')) {
              const versionParts = latestVersion.split('.');
              let nestedValue = seenFeaturesByVersion;
              for (const part of versionParts) {
                if (nestedValue && typeof nestedValue === 'object' && part in nestedValue) {
                  nestedValue = nestedValue[part];
                } else {
                  nestedValue = null;
                  break;
                }
              }
              seenFeaturesForVersion = nestedValue;
            }
            
            // Handle both array and undefined/null cases
            const hasSeenFeature = Array.isArray(seenFeaturesForVersion) 
              ? seenFeaturesForVersion.includes(featureId)
              : false;
            
            setShouldShow(!hasSeenFeature);
          } else {
            setShouldShow(false);
          }
        } else {
          setShouldShow(false);
        }
      } catch (error) {
        console.error('Error checking feature visibility:', error);
        setShouldShow(false);
      } finally {
        setLoading(false);
      }
    };

    checkFeatureVisibility();
  }, [currentUser, latestVersion, introducedInVersion, featureId]);

  const markAsSeen = async () => {
    if (!currentUser || !latestVersion || !introducedInVersion) {
      return;
    }

    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      
      // Get current seen features
      const userDocSnap = await getDoc(userDocRef);
      const currentSeenFeatures = userDocSnap.exists() 
        ? (userDocSnap.data().seenFeaturesByVersion || {})
        : {};

      // Get the array of seen features for this version, or create new array
      // Check both string key and nested structure (for backwards compatibility)
      let seenFeaturesForVersion = currentSeenFeatures[latestVersion];
      
      // If not found as string key, check for nested structure
      if (!seenFeaturesForVersion && latestVersion.includes('.')) {
        const versionParts = latestVersion.split('.');
        let nestedValue = currentSeenFeatures;
        for (const part of versionParts) {
          if (nestedValue && typeof nestedValue === 'object' && part in nestedValue) {
            nestedValue = nestedValue[part];
          } else {
            nestedValue = null;
            break;
          }
        }
        seenFeaturesForVersion = nestedValue;
      }
      
      const currentArray = Array.isArray(seenFeaturesForVersion) ? seenFeaturesForVersion : [];
      
      // Only update if feature hasn't been marked as seen yet
      if (!currentArray.includes(featureId)) {
        // Update the entire seenFeaturesByVersion object with correct string key structure
        // This will overwrite any nested structure with the correct flat structure
        const updatedSeenFeatures = {
          ...currentSeenFeatures,
          [latestVersion]: [...currentArray, featureId]
        };
        
        await updateDoc(userDocRef, {
          seenFeaturesByVersion: updatedSeenFeatures
        });

        // Immediately update local state to hide the highlight
        setShouldShow(false);
      } else {
        // Already marked as seen, just hide it
        setShouldShow(false);
      }
    } catch (error) {
      console.error('Error marking feature as seen:', error);
      // Even on error, hide the highlight to prevent it from being stuck
      setShouldShow(false);
    }
  };

  return {
    shouldShow,
    markAsSeen,
    loading
  };
};


